import { defineStore } from "pinia";
import { PaginationAttributes } from "@/interface/pagination_interface";
import { VesselScheduleRepository } from "@/repository/vessel_schedule_repository";
import { VesselSchedule } from "@/interface/schedule/vessel_schedule_interface";
import { useScheduleLocationStore } from "@/store/schedule/schedule_location_store";
import { appButtonClick } from "@/utils/analytics";

const locationStore = useScheduleLocationStore();
export const useVesselScheduleStore = defineStore("useVesselScheduleStore", {
  state: (): {
    originalSchedules: VesselSchedule[];
    filterSchedules: VesselSchedule[];
    pagination: PaginationAttributes;
    isLoading: boolean;
    vesselSearch: boolean;
    error: string | null;
  } => ({
    originalSchedules: [] as VesselSchedule[],
    filterSchedules: [] as VesselSchedule[],
    pagination: {
      totalPages: 0,
      page: 1,
      pageSize: 25,
      totalRecords: 0
    },
    isLoading: false,
    error: null,
    vesselSearch: false
  }),
  persist: false,
  getters: {
    getVesselSchedules(): VesselSchedule[] {
      return this.originalSchedules;
    },
    getPagination(): PaginationAttributes {
      return this.pagination;
    },
    getLoadingStatus(): boolean {
      return this.isLoading;
    },
    getError(): string | null {
      return this.error;
    }
  },
  actions: {
    // Making api call for the contianer history
    async fetchVesselSchedule(): Promise<void> {
      this.resetHistory();
      try {
        this.isLoading = true;
        const service = new VesselScheduleRepository();
        const schedules = await service.getVesselSchedule();
        this.vesselSearch = true;
        this.originalSchedules = schedules;
        this.filterSchedules = schedules;
        this.isLoading = false;
        this.updatePaginationCount();
        appButtonClick("Vessel Schedule", "Search Results Count", locationStore.terminalSelected.oneStreamCode, {
          responseCode: "200",
          searchedContainerCount: "N/A",
          ...locationStore.anayticsTerminalDetails
        });
      } catch (e: any) {
        appButtonClick("Vessel Schedule", "Search Results Count", locationStore.terminalSelected.oneStreamCode, {
          responseCode: e || "500",
          searchedContainerCount: "N/A",
          ...locationStore.anayticsTerminalDetails
        });
        this.isLoading = false;
        this.error = e;
      }
    },

    // Filter the schedules based on the search query
    searchFilterSchedules(searchQuery: string): void {
      const list = this.originalSchedules as VesselSchedule[];
      this.filterSchedules = list.filter((schedule): boolean => {
        return (
          schedule.vesselName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          schedule.berthName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          schedule.vesselOperator?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          schedule.vesselStatus?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
      this.updatePaginationCount();
    },

    convertDateObject(dateValueObj: any) {
      const [day, month, year] = dateValueObj.from.split("-");
      const fromDate = new Date(`${year}-${month}-${day}`);
      const toDate = new Date(`${year}-${month}-${day}`);
      return {
        fromDate,
        toDate
      };
    },

    // Function to check if a vessel record matches the date filters
    isVesselInRange: (vessel, filter) => {
      return Object.keys(filter).every((key) => {
        if (key.includes("Name") || key.includes("Status") || key.includes("Berth")) {
          if (key.includes("Name")) {
            return vessel.vesselName?.toLowerCase().includes(filter[key].toLowerCase());
          } else if (key.includes("Status")) {
            return vessel.vesselStatus?.toLowerCase().includes(filter[key].toLowerCase());
          } else if (key.includes("Berth")) {
            return vessel.berthName?.toLowerCase().includes(filter[key].toLowerCase());
          }
        } else {
          const { from, to } = filter[key];
          if (from === "" && to === "") return true;
          const vesselDate = vessel[key] ? new Date(vessel[key]) : null;

          if (!vesselDate) return false; // Skip if no date in vessel data
          else {
            const fromDate = from ? new Date(from.split("-").reverse().join("-")) : null;
            const toDate = to ? new Date(to.split("-").reverse().join("-")) : null;

            if (fromDate && vesselDate < fromDate) return false;
            if (toDate && vesselDate > toDate) return false;
          }
        }
        return true;
      });
    },

    searchVesselDrawerFilters(searchObject: any): void {
      const list = this.originalSchedules as VesselSchedule[];
      this.filterSchedules = list.filter((vessel) => this.isVesselInRange(vessel, searchObject));
      this.updatePaginationCount();
    },

    clearVesselDrawerFilters(searchObject: any): void {
      Object.keys(searchObject).forEach((key) => {
        if (key.includes("Name") || key.includes("Status") || key.includes("Berth")) {
          searchObject[key] = "";
        } else {
          searchObject[key] = { from: "", to: "" };
        }
      });
      const list = this.originalSchedules as VesselSchedule[];
      this.filterSchedules = list.filter((vessel) => this.isVesselInRange(vessel, searchObject));
      this.updatePaginationCount();
    },

    // Updating the pagination count
    updatePaginationCount(): void {
      this.pagination = {
        ...this.pagination,
        totalPages: Math.ceil(this.filterSchedules.length / this.pagination.pageSize),
        totalRecords: this.filterSchedules.length,
        pageSize: 25,
        page: 1
      };
    },

    removeFilter(): void {
      this.filterSchedules = this.originalSchedules;
      this.updatePaginationCount();
    },

    resetHistory(): void {
      this.filterSchedules = [];
      this.originalSchedules = [];
      this.pagination = {
        totalPages: 0,
        page: 1,
        pageSize: 25,
        totalRecords: 0
      };
      this.isLoading = false;
      this.error = null;
    },

    resetError(): void {
      this.error = null;
    }
  }
});
